import { Button, ButtonLink } from './Input2';
import { Alerts } from './StrokeIcons';

export type EmailCredentialsBannerFailure = {
  id: string;
  lastSyncError: string | null;
};

export type EmailCredentialsBannerPureProps = {
  /** Credentials that currently have a sync error. Banner hides when empty. */
  failedCredentials: EmailCredentialsBannerFailure[];
  /** True while the retry mutation is in flight. */
  isRetrying: boolean;
  /** Retry the first failed credential. */
  onRetry: () => void;
  /** Where "Go to Settings" links. */
  settingsHref?: string;
};

/**
 * Presentational banner shown when email credentials have authentication errors.
 * Pure: no data fetching — the container supplies the failures + retry handler.
 */
export const EmailCredentialsBannerPure = ({
  failedCredentials,
  isRetrying,
  onRetry,
  settingsHref = '/teams/active/user/me/email',
}: EmailCredentialsBannerPureProps) => {
  const firstFailed = failedCredentials[0];
  if (!firstFailed) {
    return null;
  }

  return (
    <div className="flex items-start justify-between gap-3 bg-amber-500 px-4 py-2 text-white">
      {/* The message now carries Google's real reason, so it can run long — let it wrap and clamp
          instead of squeezing the actions off the right edge. Full text on hover. */}
      <div className="flex min-w-0 flex-1 flex-wrap items-start gap-x-2 gap-y-0.5">
        <Alerts.AlertTriangle className="h-5 w-5 shrink-0" />
        <span className="shrink-0 font-medium">Email connection issue{failedCredentials.length > 1 ? 's' : ''}:</span>
        {/* `min-w` + a wrapping row: when the reason can't fit beside the label it drops to its own
            line at full width, rather than being squeezed to zero by its shrink-0 siblings. */}
        <span
          className="line-clamp-2 min-w-[12rem] flex-1 text-amber-100"
          title={firstFailed.lastSyncError ?? undefined}>
          {firstFailed.lastSyncError}
        </span>
        {failedCredentials.length > 1 && (
          <span className="shrink-0 text-amber-100">({failedCredentials.length} accounts affected)</span>
        )}
      </div>
      <div className="flex shrink-0 gap-2">
        <Button
          size="sm"
          onClick={onRetry}
          disabled={isRetrying}
          className="bg-amber-600 hover:bg-amber-700 text-white">
          {isRetrying ? 'Retrying...' : 'Retry Connection'}
        </Button>
        <ButtonLink href={settingsHref} size="sm" className="bg-amber-600 hover:bg-amber-700 text-white">
          Go to Settings
        </ButtonLink>
      </div>
    </div>
  );
};
